import React, { useEffect, useState } from "react";
import { X, Check, Loader2, Sparkles, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/AuthContext";

export default function PricingModal({ open, onClose, currentTier, onActivated }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => { if (open) api.billingPlans().then(setData).catch(() => {}); }, [open]);
  if (!open) return null;

  const pay = async (plan) => {
    if (!data?.paymentsConfigured) {
      toast.message("Online payments aren't enabled yet on this workspace.");
      return;
    }
    if (!window.Razorpay) { toast.error("Payment module failed to load. Please retry."); return; }
    try {
      setBusy(plan.id);
      const order = await api.createOrder(plan.id);
      const rzp = new window.Razorpay({
        key: order.keyId,
        amount: order.amount,
        currency: order.currency,
        name: "SSG AI",
        description: `${plan.label} — monthly subscription`,
        order_id: order.orderId,
        prefill: { name: user?.name, email: user?.email, contact: user?.mobile },
        theme: { color: "#10B981" },
        handler: async (res) => {
          try {
            await api.verifyPayment({
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
              planId: plan.id,
            });
            toast.success(`${plan.label} activated`);
            onActivated?.();
            onClose();
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment verification failed");
          }
        },
        modal: { ondismiss: () => setBusy(null) },
      });
      rzp.on("payment.failed", () => { toast.error("Payment failed. You were not charged for an activated plan."); setBusy(null); });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment");
    } finally {
      setBusy(null);
    }
  };

  const free = data?.free;

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-start justify-center p-4 overflow-y-auto" data-testid="pricing-modal">
      <div className="bg-[#0B0F17] border border-white/10 rounded-2xl w-full max-w-4xl my-8 shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2.5"><Sparkles size={18} className="text-emerald-400" /><span className="font-head font-bold text-white">Plans & pricing</span></div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="close-pricing-modal"><X size={18} /></button>
        </div>

        <div className="p-5">
          {!data && <div className="flex items-center gap-2 text-slate-400 text-sm py-10 justify-center"><Loader2 size={16} className="animate-spin" /> Loading plans…</div>}
          {data && (
            <>
              {!data.paymentsConfigured && (
                <div className="mb-4 text-[12px] text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2" data-testid="payments-not-configured">
                  Online payments are being finalized for this workspace. Plans are shown for reference.
                </div>
              )}
              <div className="grid md:grid-cols-3 gap-3">
                <PlanCard name="Free" price={0} label="Free" current={currentTier === "Free"}
                  features={[`${free?.aiQuestions ?? 10} AI questions / 24h`, "Data analysis", "Reports & exports", "1 workspace"]} />
                {data.plans.map((p) => (
                  <PlanCard key={p.id} name={p.tier} price={p.priceInr} label={p.label} gst={p.gstInr}
                    current={currentTier === p.tier} busy={busy === p.id} onSelect={() => pay(p)}
                    features={[`${p.limits.ai_messages.toLocaleString()} AI questions`,
                               `${p.limits.image_generation} images`, `${p.limits.deep_research} deep research`,
                               `${p.limits.data_analysis.toLocaleString()} analyses`]} />
                ))}
              </div>
              <div className="mt-4 text-[11px] text-slate-500 flex items-center gap-1.5">
                <ShieldCheck size={12} className="text-emerald-400" /> Prices in {data.currency} inclusive of {data.gstPercent}% GST · Secure payments via Razorpay · You can cancel anytime.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PlanCard({ name, price, label, gst, features, current, busy, onSelect }) {
  return (
    <div className={`rounded-xl border p-4 flex flex-col ${current ? "border-emerald-500/50 bg-emerald-500/5" : "border-white/10 bg-[#111827]"}`} data-testid={`plan-${name.toLowerCase()}`}>
      <div className="text-sm font-semibold text-white">{label}</div>
      <div className="mt-1 mb-3">
        <span className="text-2xl font-head font-extrabold text-white">₹{price.toLocaleString()}</span>
        {price > 0 && <span className="text-[11px] text-slate-500">/mo</span>}
        {gst ? <div className="text-[10px] text-slate-500">incl. ₹{gst} GST</div> : null}
      </div>
      <ul className="space-y-1.5 flex-1">
        {features.map((f, i) => (
          <li key={i} className="flex items-center gap-1.5 text-[12px] text-slate-300"><Check size={13} className="text-emerald-400 shrink-0" /> {f}</li>
        ))}
      </ul>
      {current ? (
        <div className="mt-3 text-center text-[12px] text-emerald-300 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">Current plan</div>
      ) : price === 0 ? (
        <div className="mt-3 text-center text-[12px] text-slate-500 py-2">Default</div>
      ) : (
        <button data-testid={`upgrade-${name.toLowerCase()}`} onClick={onSelect} disabled={busy}
          className="mt-3 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin" /> : "Upgrade"}
        </button>
      )}
    </div>
  );
}
